import { supabase } from '../lib/supabase';

/**
 * Utility functions for handling web push notifications
 * 
 * This module provides a comprehensive set of functions for managing web push notifications,
 * including permission handling, subscription management, and notification preferences.
 * 
 * The system uses the Web Push API with service workers to deliver notifications even when
 * the app is closed or in the background.
 */

/**
 * Check if the browser supports notifications
 */
export const checkNotificationSupport = (): boolean => {
  return 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
};

/**
 * Check the current notification permission status
 */
export const checkNotificationPermission = (): NotificationPermission => {
  if (!checkNotificationSupport()) {
    return 'denied';
  }
  
  return Notification.permission;
};

/**
 * Convert a base64 string to a Uint8Array for push subscription
 */
export const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
  try {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    
    return outputArray;
  } catch (error) {
    throw new Error('Failed to process application server key');
  }
};

/**
 * Register a service worker and subscribe to push notifications
 * Note: This assumes permission has already been granted
 */
export const subscribeUserToPush = async (): Promise<PushSubscription | null> => {
  try {
    // Check browser support
    if (!checkNotificationSupport()) {
      throw new Error('Push notifications are not supported in this browser');
    }
    
    // Check if permission is already granted
    if (Notification.permission !== 'granted') {
      return null;
    }
    
    // Register service worker or get existing registration
    let registration: ServiceWorkerRegistration;
    
    try {
      // Try to get existing registration first
      registration = await navigator.serviceWorker.getRegistration('/service-worker.js');
      
      // If no registration exists, register the service worker
      if (!registration) {
        registration = await navigator.serviceWorker.register('/service-worker.js', {
          scope: '/'
        });
      }
    } catch (regError) {
      // Try one more time with a simpler registration
      registration = await navigator.serviceWorker.register('/service-worker.js');
    }
    
    // Wait for the service worker to be ready
    await navigator.serviceWorker.ready;
    
    // Get VAPID public key from environment
    const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
    
    if (!vapidPublicKey) {
      throw new Error('VAPID public key not found in environment variables');
    }
    
    // Convert VAPID key to Uint8Array
    const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);
    
    // Get current subscription
    let subscription = await registration.pushManager.getSubscription();
    
    // If a subscription exists, use it unless it's expired
    if (subscription) {
      try {
        // Test if the subscription is still valid with a simple operation
        const endpoint = subscription.endpoint;
        if (endpoint) {
          return subscription;
        }
      } catch (subError) {
        // If there's an error accessing the subscription, it might be invalid
        // Unsubscribe and create a new one
        await subscription.unsubscribe().catch(e => {});
        subscription = null;
      }
    }
    
    // Create a new subscription if needed
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey
      });
    }
    
    return subscription;
  } catch (error) {
    return null;
  }
};

/**
 * Save a push subscription to the database
 */
export const savePushSubscription = async (subscription: PushSubscription): Promise<boolean> => {
  try {
    // Get current user
    const { data: userData } = await supabase.auth.getUser();
    const user = userData?.user;
    
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    // Convert subscription to JSON to extract keys
    const subscriptionJSON = JSON.parse(JSON.stringify(subscription));
    
    // Save subscription to database
    const { error: subscriptionError } = await supabase
      .from('push_subscriptions')
      .upsert({
        user_id: user.id,
        endpoint: subscription.endpoint,
        p256dh: subscriptionJSON.keys.p256dh,
        auth: subscriptionJSON.keys.auth,
        user_agent: navigator.userAgent,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'user_id, endpoint'
      });
    
    if (subscriptionError) {
      throw subscriptionError;
    }
    
    // Update user preferences
    const { error: preferencesError } = await supabase
      .from('notification_preferences')
      .upsert({
        user_id: user.id,
        push_notifications: true,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'user_id'
      });
    
    if (preferencesError) {
      throw preferencesError;
    }
    
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Unsubscribe from push notifications
 */
export const unsubscribeFromPush = async (): Promise<boolean> => {
  try {
    // Check if service worker is registered
    const registration = await navigator.serviceWorker.getRegistration();
    
    if (!registration) {
      return false;
    }
    
    // Get current subscription
    const subscription = await registration.pushManager.getSubscription();
    
    if (!subscription) {
      return false;
    }
    
    // Unsubscribe
    const success = await subscription.unsubscribe();
    
    if (success) {
      // Get current user
      const { data: userData } = await supabase.auth.getUser();
      const user = userData?.user;
      
      if (user) {
        // Remove subscription from database
        await supabase
          .from('push_subscriptions')
          .delete()
          .eq('user_id', user.id)
          .eq('endpoint', subscription.endpoint);
        
        // Update user preferences
        await supabase
          .from('notification_preferences')
          .update({
            push_notifications: false,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', user.id);
      }
    }
    
    return success;
  } catch (error) {
    return false;
  }
};

/**
 * Register service worker for push notifications
 */
export const registerServiceWorker = async (): Promise<ServiceWorkerRegistration | null> => {
  if (!('serviceWorker' in navigator)) {
    return null;
  }
  
  try {
    const registration = await navigator.serviceWorker.register('/service-worker.js');
    return registration;
  } catch (error) {
    return null;
  }
};

/**
 * Get user notification preferences
 */
export const getUserNotificationPreferences = async () => {
  const { data: userData } = await supabase.auth.getUser();
  const user = userData?.user;
  
  if (!user) {
    return null;
  }
  
  const { data, error } = await supabase
    .from('notification_preferences')
    .select('*')
    .eq('user_id', user.id)
    .single();
  
  if (error) {
    return null;
  }
  
  return data;
};

/**
 * Update user notification preferences
 */
export const updateNotificationPreferences = async (preferences: {
  order_status_notifications?: boolean;
  promotion_notifications?: boolean;
  announcement_notifications?: boolean;
  payment_notifications?: boolean;
  email_notifications?: boolean;
  push_notifications?: boolean;
}) => {
  const { data: userData } = await supabase.auth.getUser();
  const user = userData?.user;
  
  if (!user) {
    return false;
  }
  
  const { error } = await supabase
    .from('notification_preferences')
    .upsert({
      user_id: user.id,
      ...preferences,
      updated_at: new Date().toISOString()
    }, {
      onConflict: 'user_id'
    });
  
  if (error) {
    return false;
  }
  
  return true;
};

/**
 * Get unread notifications for current user
 */
export const getUnreadNotifications = async () => {
  const { data: userData } = await supabase.auth.getUser();
  const user = userData?.user;
  
  if (!user) {
    return [];
  }
  
  const { data, error } = await supabase
    .from('notifications')
    .select('*')
    .eq('user_id', user.id)
    .is('read_at', null)
    .order('created_at', { ascending: false });
  
  if (error) {
    return [];
  }
  
  return data || [];
};

/**
 * Mark a notification as read
 */
export const markNotificationAsRead = async (notificationId: string) => {
  const { error } = await supabase
    .from('notifications')
    .update({
      read_at: new Date().toISOString()
    })
    .eq('id', notificationId);
  
  if (error) {
    return false;
  }
  
  return true;
};

/**
 * Mark all notifications as read
 */
export const markAllNotificationsAsRead = async () => {
  const { data: userData } = await supabase.auth.getUser();
  const user = userData?.user;
  
  if (!user) {
    return false;
  }
  
  const { error } = await supabase
    .from('notifications')
    .update({
      read_at: new Date().toISOString()
    })
    .eq('user_id', user.id)
    .is('read_at', null);
  
  if (error) {
    return false;
  }
  
  return true;
};

/**
 * Send a notification directly to a user using Supabase Edge Function
 * This is used internally by the system and not directly exposed to the UI
 */
export const sendPushNotification = async (
  subscription: PushSubscription,
  notification: {
    title: string;
    body: string;
    icon?: string;
    image?: string;
    data?: Record<string, any>;
  }
): Promise<boolean> => {
  try {
    // Get current user
    const { data: userData } = await supabase.auth.getUser();
    const user = userData?.user;
    
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    // Get JWT token for authorization
    const { data: authData } = await supabase.auth.getSession();
    const token = authData?.session?.access_token;
    
    if (!token) {
      throw new Error('No valid session token');
    }
    
    // Convert subscription to JSON format for the Edge Function
    const subscriptionJSON = JSON.parse(JSON.stringify(subscription));
    
    // Call the Edge Function to send the notification
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-push-notification`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          subscription: {
            endpoint: subscription.endpoint,
            p256dh: subscriptionJSON.keys.p256dh,
            auth: subscriptionJSON.keys.auth
          },
          notification
        })
      }
    );
    
    const result = await response.json();
    
    if (!result.success) {
      // If the subscription is invalid, remove it from the database
      if (result.shouldRemove) {
        await supabase
          .from('push_subscriptions')
          .delete()
          .eq('endpoint', subscription.endpoint);
      }
      
      throw new Error(result.error || 'Failed to send notification');
    }
    
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Synchronize any pending subscription updates saved by the service worker
 * This should be called when the app starts, especially after a user logs in
 */
export const syncPendingSubscriptions = async (): Promise<boolean> => {
  if (!('indexedDB' in window)) {
    return false;
  }
  
  try {
    // Open the database
    const dbPromise = await new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open('zap-push-subscriptions', 1);
      
      request.onupgradeneeded = (event) => {
        const db = request.result;
        if (!db.objectStoreNames.contains('pending-subscriptions')) {
          db.createObjectStore('pending-subscriptions', { keyPath: 'id', autoIncrement: true });
        }
      };
      
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    
    // Get all pending subscription updates
    const pendingUpdates = await new Promise<any[]>((resolve, reject) => {
      const transaction = dbPromise.transaction('pending-subscriptions', 'readonly');
      const store = transaction.objectStore('pending-subscriptions');
      const request = store.getAll();
      
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    
    if (!pendingUpdates || pendingUpdates.length === 0) {
      dbPromise.close();
      return true;
    }
    
    // Get auth token
    const { data: authData } = await supabase.auth.getSession();
    const token = authData?.session?.access_token;
    
    if (!token) {
      dbPromise.close();
      return false;
    }
    
    // Process each pending update
    for (const update of pendingUpdates) {
      try {
        // Use the environment variable instead of hardcoded URL
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        
        const response = await fetch(`${supabaseUrl}/functions/v1/update-push-subscription`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            oldEndpoint: update.oldEndpoint,
            newSubscription: update.newSubscription,
            userToken: token
          })
        });
        
        const result = await response.json();
        
        if (result.success) {
          // Delete the processed update from IndexedDB
          const deleteTransaction = dbPromise.transaction('pending-subscriptions', 'readwrite');
          const deleteStore = deleteTransaction.objectStore('pending-subscriptions');
          deleteStore.delete(update.id);
          
          await new Promise<void>(resolve => {
            deleteTransaction.oncomplete = () => resolve();
            deleteTransaction.onerror = () => resolve(); // Continue even if delete fails
          });
        }
      } catch (err) {
        // Silently handle errors for individual updates
      }
    }
    
    dbPromise.close();
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Helper function to send a test notification (development only)
 */
export const sendTestNotification = async (): Promise<boolean> => {
  if (import.meta.env.PROD) {
    return false;
  }
  
  try {
    // Check permission first
    if (Notification.permission !== 'granted') {
      const permissionResult = await Notification.requestPermission();
      
      if (permissionResult !== 'granted') {
        throw new Error('Notification permission not granted');
      }
    }
    
    // Register service worker if not already registered
    const registration = await registerServiceWorker();
    
    if (!registration) {
      throw new Error('Service worker not registered');
    }
    
    // Get subscription
    let subscription = await registration.pushManager.getSubscription();
    
    if (!subscription) {
      subscription = await subscribeUserToPush();
      
      if (!subscription) {
        throw new Error('Failed to subscribe to push notifications');
      }
    }
    
    // If we're in development, show a local notification immediately as well
    // This helps with debugging independent of the server push
    if (import.meta.env.DEV) {
      // Using the Notification API directly as a test
      try {
        const localNotification = new Notification('Local Test Notification', {
          body: 'This is a local test (not push) notification',
          icon: '/zap-logo.svg'
        });
        
        localNotification.onclick = () => {
          window.focus();
        };
      } catch (localError) {
      }
    }
    
    // Send test notification via the server
    return await sendPushNotification(subscription, {
      title: 'Test Push Notification',
      body: 'This is a test push notification from Zap',
      data: {
        url: '/profile',
        test: true
      }
    });
  } catch (error) {
    return false;
  }
};